import _XIcon from "@adsk/alloy-react-icon/es/lib/XIcon";
import _XCircleIcon from "@adsk/alloy-react-icon/es/lib/XCircleIcon";
import _AlertWarningIcon from "@adsk/alloy-react-icon/es/lib/AlertWarningIcon";
import _CheckmarkCircleIcon from "@adsk/alloy-react-icon/es/lib/CheckmarkCircleIcon";
import _AlertInformationIcon from "@adsk/alloy-react-icon/es/lib/AlertInformationIcon";
import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
const _excluded = ["style", "className", "renderIcon", "variant", "onClose", "message", "open", "inline", "children"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { BasicButton } from '@adsk/alloy-react-button';
import { variantSwitch } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import BannerDivider from './BannerDivider';
import { BANNER_VARIANTS } from './consts';
export const mapVariantToIconRenderer = {
  [BANNER_VARIANTS.PRIMARY]: _AlertInformationIcon.renderIcon,
  [BANNER_VARIANTS.SUCCESS]: _CheckmarkCircleIcon.renderIcon,
  [BANNER_VARIANTS.WARNING]: _AlertWarningIcon.renderIcon,
  [BANNER_VARIANTS.ERROR]: _XCircleIcon.renderIcon
};
export const mapVariantToBackgroundColor = {
  [BANNER_VARIANTS.PRIMARY]: theme.tokens.colors.background.information.subtle.value,
  [BANNER_VARIANTS.SUCCESS]: theme.tokens.colors.background.success.subtle.value,
  [BANNER_VARIANTS.WARNING]: theme.tokens.colors.background.warning.subtle.value,
  [BANNER_VARIANTS.ERROR]: theme.tokens.colors.background.error.subtle.value
};
export const mapVariantToBorderColor = {
  [BANNER_VARIANTS.PRIMARY]: theme.tokens.colors.border.information.value,
  [BANNER_VARIANTS.SUCCESS]: theme.tokens.colors.border.success.value,
  [BANNER_VARIANTS.WARNING]: theme.tokens.colors.border.warning.value,
  [BANNER_VARIANTS.ERROR]: theme.tokens.colors.border.error.value
};
const VISIBLE_STATE = 'visible';
const HIDDEN_STATE = 'hidden';

/**
 * Banners are notifications alerting an event that requires a user action.
 * They are similar toasts except banners are not sparked by the user’s action,
 * therefore they require a different design.
 * Banners are primarily used for global messages about the system that can be simply informational (blue)
 *  or color coded to signal success (green), warning (yellow) and error (red).
 */
const Banner = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var _mapVariantToIconRend, _T$translate;
  let {
      style,
      className,
      renderIcon,
      variant = BANNER_VARIANTS.PRIMARY,
      onClose,
      message,
      open = true,
      inline,
      children
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require(`./i18n/${locale}.json`);
  });
  const [visibleState, setVisibleState] = useState(VISIBLE_STATE);
  const transitionStart = useCallback(() => {
    if (open) {
      setVisibleState(VISIBLE_STATE);
    }
  }, [open]);
  useEffect(() => {
    document.addEventListener('transitionstart', transitionStart);
    if (open) {
      setVisibleState(VISIBLE_STATE);
    } else {
      setVisibleState(HIDDEN_STATE);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('transitionstart', transitionStart);
    };

    // we only want to react to changes in the open prop on component mounted
    // because subsequent changes of open need to be handled by the transition
    // start and complete handlers to maintain the animation feature of the banner
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (open) {
      setVisibleState(VISIBLE_STATE);
    }
  }, [open]);
  const transitionComplete = useCallback(() => {
    if (!open) {
      setVisibleState(HIDDEN_STATE);
    }
  }, [open]);
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({}, props, {
    ref: ref,
    className: className,
    onTransitionEnd: transitionComplete,
    $_css: [_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      width: '100%',
      display: 'flex',
      visibility: visibleState,
      minHeight: '50px',
      color: theme.tokens.colors.text.primary.value,
      backgroundColor: mapVariantToBackgroundColor[variant],
      transitionDuration: '0.3s',
      transitionProperty: 'transform',
      border: `1px solid ${mapVariantToBorderColor[variant]}`,
      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
    }, !inline && {
      position: 'absolute',
      top: 0,
      left: 0
    }), inline && {
      borderRadius: theme.borderRadius.medium
    }), open && {
      transform: 'translateY(0)'
    }), !open && {
      transform: 'translateY(-100%)'
    }), style]
  }), /*#__PURE__*/React.createElement(_StyledDiv2, {
    $_css2: [{
      minHeight: 40,
      height: 'auto',
      minWidth: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.tokens.colors.icon.inverse.value
    }, variantSwitch({
      statement: variant,
      variants: {
        [BANNER_VARIANTS.PRIMARY]: {
          backgroundColor: theme.tokens.colors.background.information.value
        },
        [BANNER_VARIANTS.SUCCESS]: {
          backgroundColor: theme.tokens.colors.background.success.value
        },
        [BANNER_VARIANTS.WARNING]: {
          backgroundColor: theme.tokens.colors.background.warning.value
        },
        [BANNER_VARIANTS.ERROR]: {
          backgroundColor: theme.tokens.colors.background.error.value
        }
      }
    })]
  }, (renderIcon === null || renderIcon === void 0 ? void 0 : renderIcon({})) || ((_mapVariantToIconRend = mapVariantToIconRenderer[variant]) === null || _mapVariantToIconRend === void 0 ? void 0 : _mapVariantToIconRend.call(mapVariantToIconRenderer, {}))), message && /*#__PURE__*/React.createElement(_StyledP, {
    $_css3: [{
      paddingLeft: 15,
      flex: 1
    }]
  }, message), /*#__PURE__*/React.createElement(_StyledDiv3, {
    $_css4: [{
      height: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      flewDirection: 'row',
      paddingLeft: message ? 0 : 15,
      '& button': {
        margin: '0 5px'
      }
    }]
  }, children), children && onClose && /*#__PURE__*/React.createElement(BannerDivider, {
    variant: variant
  }), onClose && /*#__PURE__*/React.createElement(BasicButton, {
    onClick: onClose,
    style: {
      marginRight: 6
    },
    "aria-label": (_T$translate = T.translate('CLOSE_BANNER')) === null || _T$translate === void 0 ? void 0 : _T$translate.toString()
  }, /*#__PURE__*/React.createElement(_XIcon, null)));
});
Banner.displayName = 'Banner';
Banner.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** render prop to display an icon on the left */
  renderIcon: PropTypes.func,
  /** Banner's variant, defaults to 'primary' */
  variant: PropTypes.oneOf(Object.values(BANNER_VARIANTS)),
  /** Banner's message to display */
  message: PropTypes.any,
  /** Toggles the Banner's visibility */
  open: PropTypes.bool,
  /** A Trigger for the close button in the Banner */
  onClose: PropTypes.func,
  /** Whether or not to use inline Banner (rounded corners, etc.)  */
  inline: PropTypes.bool,
  /** The component's children, here you should put the actions */
  children: PropTypes.any
};
export default Object.assign(Banner, {
  VARIANTS: BANNER_VARIANTS
});
var _StyledDiv = _styled("div")`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div")`${p => p.$_css2}`;
var _StyledP = _styled3("p")`${p => p.$_css3}`;
var _StyledDiv3 = _styled4("div").withConfig({
  displayName: "Banner___StyledDiv3",
  componentId: "sc-13lgpjx-0"
})(["", ""], p => p.$_css4);
//# sourceMappingURL=Banner.js.map